import React, { useState, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Bold, Italic, List, Link2, Video, AtSign } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

export function RichEditor(props) {
  var value = props.value;
  var onChange = props.onChange;
  var placeholder = props.placeholder;
  var className = props.className;
  var onMentionsChange = props.onMentionsChange;
  var testId = props['data-testid'];

  var auth = useAuth();
  var getAuthHeaders = auth.getAuthHeaders;
  var textareaRef = useRef(null);
  var searchTimeoutRef = useRef(null);
  var mentionListRef = useRef([]);

  var showMentionsState = useState(false);
  var showMentions = showMentionsState[0];
  var setShowMentions = showMentionsState[1];

  var mentionResultsState = useState([]);
  var mentionResults = mentionResultsState[0];
  var setMentionResults = mentionResultsState[1];

  function handleWrap(before, after) {
    var ta = textareaRef.current;
    if (!ta) return;
    var s = ta.selectionStart;
    var e = ta.selectionEnd;
    var t = value || '';
    onChange(t.substring(0, s) + before + t.substring(s, e) + after + t.substring(e));
  }

  function handleChange(e) {
    var text = e.target.value;
    onChange(text);
    var cursor = e.target.selectionStart;
    var before = text.substring(0, cursor);
    var atIdx = before.lastIndexOf('@');
    if (atIdx >= 0) {
      var query = before.substring(atIdx + 1);
      if (query.length >= 1 && query.indexOf(' ') === -1) {
        setShowMentions(true);
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(function() {
          axios.get(API_BASE + '/users/mention-search?q=' + encodeURIComponent(query), {
            headers: getAuthHeaders(), withCredentials: true
          }).then(function(res) { setMentionResults(res.data); }).catch(function() { setMentionResults([]); });
        }, 200);
        return;
      }
    }
    setShowMentions(false);
  }

  function doInsertMention(user) {
    var ta = textareaRef.current;
    if (!ta) return;
    var cursor = ta.selectionStart;
    var text = value || '';
    var before = text.substring(0, cursor);
    var atIdx = before.lastIndexOf('@');
    var newText = before.substring(0, atIdx) + '@' + user.name + ' ' + text.substring(cursor);
    onChange(newText);
    setShowMentions(false);
    mentionListRef.current = mentionListRef.current.concat(user.user_id);
    if (onMentionsChange) onMentionsChange(mentionListRef.current);
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-0.5 px-1">
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={function() { handleWrap('**', '**'); }}><Bold className="h-3.5 w-3.5" /></Button>
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={function() { handleWrap('_', '_'); }}><Italic className="h-3.5 w-3.5" /></Button>
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={function() { handleWrap('\n- ', ''); }}><List className="h-3.5 w-3.5" /></Button>
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={function() {
          var url = prompt('Paste YouTube or video URL:');
          if (url) onChange((value || '') + '\n[video:' + url + ']');
        }}><Video className="h-3.5 w-3.5" /></Button>
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={function() {
          var ta = textareaRef.current;
          if (ta) { var pos = ta.selectionStart; onChange((value || '').substring(0, pos) + '@' + (value || '').substring(pos)); }
        }}><AtSign className="h-3.5 w-3.5" /></Button>
      </div>
      <div className="relative">
        <Textarea
          ref={textareaRef}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          className={'min-h-[80px] resize-none ' + (className || 'input-default')}
          data-testid={testId}
        />
        {showMentions && mentionResults.length > 0 && (
          <div className="absolute left-0 bottom-full mb-1 w-64 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden" data-testid="mention-dropdown">
            {mentionResults.map(function(u) {
              return (
                <div key={u.user_id} className="flex items-center gap-2 px-3 py-2 hover:bg-muted/50 cursor-pointer text-sm" onClick={function() { doInsertMention(u); }}>
                  <Avatar className="h-6 w-6"><AvatarImage src={u.picture} /><AvatarFallback className="text-[10px]">{(u.name || '?').charAt(0)}</AvatarFallback></Avatar>
                  <span className="font-medium">{u.name}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export function RichContent(props) {
  var content = props.content;
  var format = props.format;
  if (!content) return null;
  if (format !== 'rich') return React.createElement('p', { className: 'whitespace-pre-wrap text-sm sm:text-base' }, content);

  var lines = content.split('\n');
  return React.createElement('div', { className: 'space-y-1' },
    lines.map(function(line, i) {
      if (line.indexOf('[video:') === 0 || line.indexOf('[video:') > -1) {
        var start = line.indexOf('[video:');
        var end = line.indexOf(']', start);
        if (start >= 0 && end > start) {
          var url = line.substring(start + 7, end);
          var ytMatch = url.indexOf('youtube.com') > -1 || url.indexOf('youtu.be') > -1;
          if (ytMatch) {
            var id = '';
            var vIdx = url.indexOf('v=');
            if (vIdx > -1) id = url.substring(vIdx + 2).split('&')[0];
            else { var parts = url.split('/'); id = parts[parts.length - 1]; }
            if (id) return React.createElement('div', { key: i, className: 'my-2 rounded-lg overflow-hidden aspect-video' },
              React.createElement('iframe', { src: 'https://www.youtube.com/embed/' + id, className: 'w-full h-full', allowFullScreen: true, title: 'video' }));
          }
          return React.createElement('div', { key: i, className: 'my-2' },
            React.createElement('video', { src: url, controls: true, className: 'w-full rounded-lg max-h-96' }));
        }
      }
      if (line.substring(0, 2) === '- ') {
        return React.createElement('li', { key: i, className: 'ml-4 text-sm list-disc' }, line.substring(2));
      }
      if (line.trim() === '') return React.createElement('br', { key: i });
      return React.createElement('p', { key: i, className: 'text-sm sm:text-base' }, formatText(line));
    })
  );
}

function formatText(text) {
  var parts = [];
  var key = 0;
  var i = 0;
  while (i < text.length) {
    if (text[i] === '*' && text[i+1] === '*') {
      var end = text.indexOf('**', i + 2);
      if (end > -1) {
        parts.push(React.createElement('strong', { key: key++ }, text.substring(i + 2, end)));
        i = end + 2;
        continue;
      }
    }
    if (text[i] === '_') {
      var end2 = text.indexOf('_', i + 1);
      if (end2 > -1) {
        parts.push(React.createElement('em', { key: key++ }, text.substring(i + 1, end2)));
        i = end2 + 1;
        continue;
      }
    }
    if (text[i] === '@') {
      var word = '';
      var j = i + 1;
      while (j < text.length && text[j] !== ' ' && text[j] !== '\n') { word += text[j]; j++; }
      if (word) {
        parts.push(React.createElement('span', { key: key++, className: 'text-primary font-medium' }, '@' + word));
        i = j;
        continue;
      }
    }
    // Accumulate plain text - advance at least one character to prevent infinite loop
    var plain = '';
    var startI = i;
    while (i < text.length && !(text[i] === '*' && text[i+1] === '*') && !(text[i] === '_' && text.indexOf('_', i + 1) > -1) && text[i] !== '@') { plain += text[i]; i++; }
    if (i === startI) { plain += text[i]; i++; }
    if (plain) parts.push(React.createElement('span', { key: key++ }, plain));
  }
  return parts;
}
